import type { SerializedNode, ConversionContext, RGBA, Gradient } from '../types';
import type { BackgroundImageMode, BackgroundPosition } from '../../types/layout';
import {
  ANIMATION_TYPES,
  ANIMATION_EASINGS,
  HOVER_EFFECT_TYPES,
  type AnimationType,
  type AnimationEasing,
  type HoverEffectType,
} from '../../types/animation';
import { getAttribute } from '../parser';
import {
  parseColor,
  parsePadding,
  parseBorderRadius,
  parseInteger,
  parseFloat_,
  parseValueWithUnit,
  parsePosition,
  parsePositionOffset,
  inferPositionAnchorsState,
  parseShadowAttributes,
  SHADOW_ATTRIBUTES,
  type PositionValue,
} from '../utils';
import { checkIsGradient } from '../../utils/styleHelpers';
import {
  SECTION_DEFAULTS,
  ROW_DEFAULTS,
  COLUMN_DEFAULTS,
  getContextAwareDefaults,
  MOBILE_WIDTH_DEFAULTS,
} from '../utils/defaults';
import { CONTENT_BLOCK_NAME } from '../../utils/constants';

/**
 * Container type for differentiated defaults
 */
export type ContainerType = 'section' | 'row' | 'column';

/**
 * Get defaults based on container type
 */
const getDefaults = (type: ContainerType) => {
  switch (type) {
    case 'section':
      return SECTION_DEFAULTS;
    case 'row':
      return ROW_DEFAULTS;
    case 'column':
      return COLUMN_DEFAULTS;
  }
};

/**
 * Parse breakpoint-prefixed attributes (tablet-*, mobile-*) from an element
 *
 * Uses hyphen separator (e.g., mobile-padding, tablet-gap) to avoid XML namespace
 * conflicts that occur with colon separators.
 *
 * @param element - The XML element
 * @param type - Container type (section/row/column)
 * @param defaults - Default values for the container type
 * @returns Object with tablet and mobile override props
 */
const parseBreakpointAttributes = (
  element: Element,
  defaults: ReturnType<typeof getDefaults>,
): { tablet: Record<string, unknown>; mobile: Record<string, unknown> } => {
  const tablet: Record<string, unknown> = {};
  const mobile: Record<string, unknown> = {};

  // Convert NamedNodeMap to array for iteration
  const attrs = Array.from(element.attributes);

  for (const attr of attrs) {
    // Check for tablet- or mobile- prefix
    let prefix: 'tablet' | 'mobile' | null = null;
    let propName: string | null = null;

    if (attr.name.startsWith('tablet-')) {
      prefix = 'tablet';
      propName = attr.name.slice(7); // 'tablet-'.length = 7
    } else if (attr.name.startsWith('mobile-')) {
      prefix = 'mobile';
      propName = attr.name.slice(7); // 'mobile-'.length = 7
    }

    if (!prefix || !propName) continue;

    const target = prefix === 'tablet' ? tablet : mobile;

    switch (propName) {
      case 'padding':
        target.padding = parsePadding(attr.value, defaults.padding);
        break;
      case 'margin':
        target.margin = parsePadding(attr.value, defaults.margin);
        break;
      case 'gap':
        target.gap = parseInteger(attr.value, defaults.gap);
        break;
      case 'direction':
        target.flexDirection = mapDirection(attr.value) || defaults.flexDirection;
        break;
      case 'justify':
        target.justifyContent = mapAlignJustify(attr.value) || defaults.justifyContent;
        break;
      case 'align':
        target.alignItems = mapAlignJustify(attr.value) || defaults.alignItems;
        break;
      case 'width':
        // Width override also sets flexBasis and fillSpace for consistent behavior
        target.width = attr.value;
        target.flexBasis = attr.value;
        // If width is 100%, use fillSpace: no; otherwise use yes for flexible sizing
        target.fillSpace = attr.value === '100%' ? 'no' : 'yes';
        break;
      case 'borderRadius':
        target.borderRadius = parseBorderRadius(attr.value, defaults.borderRadius);
        break;
      case 'height':
        target.height = attr.value;
        break;
      case 'maxWidth': {
        // 100% maxWidth means "disable maxWidth" (full width)
        if (attr.value === '100%') {
          target.disableMaxWidth = true;
        } else {
          const parsed = parseValueWithUnit(attr.value, defaults.maxWidth, defaults.maxWidthUnit);
          // Convert percentage values to pixels using 1200 as the base (default maxWidth)
          if (parsed.unit === '%') {
            target.maxWidth = Math.round((parsed.value / 100) * 1200);
            target.maxWidthUnit = 'px';
          } else {
            target.maxWidth = parsed.value;
            target.maxWidthUnit = parsed.unit;
          }
        }
        break;
      }
      case 'position': {
        const parsedPosition = parsePosition(attr.value);
        if (parsedPosition) target.position = parsedPosition;
        break;
      }
      case 'top':
        target.top = parsePositionOffset(attr.value);
        break;
      case 'left':
        target.left = parsePositionOffset(attr.value);
        break;
      case 'right':
        target.right = parsePositionOffset(attr.value);
        break;
      case 'bottom':
        target.bottom = parsePositionOffset(attr.value);
        break;
      case 'zIndex':
        target.zIndex = parseInteger(attr.value, 0);
        break;
    }
  }

  // Infer positionAnchorsState for each breakpoint if position props are provided
  if (
    tablet.position !== undefined ||
    tablet.top !== undefined ||
    tablet.left !== undefined ||
    tablet.right !== undefined ||
    tablet.bottom !== undefined
  ) {
    tablet.positionAnchorsState = inferPositionAnchorsState(
      tablet.position as PositionValue | undefined,
      tablet.top as string | undefined,
      tablet.left as string | undefined,
      tablet.right as string | undefined,
      tablet.bottom as string | undefined,
    );
  }
  if (
    mobile.position !== undefined ||
    mobile.top !== undefined ||
    mobile.left !== undefined ||
    mobile.right !== undefined ||
    mobile.bottom !== undefined
  ) {
    mobile.positionAnchorsState = inferPositionAnchorsState(
      mobile.position as PositionValue | undefined,
      mobile.top as string | undefined,
      mobile.left as string | undefined,
      mobile.right as string | undefined,
      mobile.bottom as string | undefined,
    );
  }

  return { tablet, mobile };
};

/**
 * Parse container attributes from an element with context-aware width defaults
 *
 * @param element - The XML element
 * @param type - Container type (section/row/column)
 * @param context - Conversion context with parent information for width calculation
 */
const parseContainerAttributes = (
  element: Element,
  type: ContainerType,
  context: ConversionContext,
): Record<string, unknown> => {
  const defaults = getDefaults(type);

  // Get explicit width attribute if provided
  const widthAttr = getAttribute(element, 'width');

  // Calculate context-aware width/flexBasis/fillSpace defaults
  const contextDefaults = getContextAwareDefaults(
    context.parentFlexDirection,
    widthAttr || undefined,
    context.calculatedWidth,
  );

  // Parse layout attributes
  const alignAttr = getAttribute(element, 'align');
  const alignItems = mapAlignJustify(alignAttr) || defaults.alignItems;

  const justifyAttr = getAttribute(element, 'justify');
  const justifyContent = justifyAttr || defaults.justifyContent;

  const gapAttr = getAttribute(element, 'gap');
  const gap = parseInteger(gapAttr, defaults.gap);

  // Parse direction attribute (section only)
  const directionAttr = type === 'section' ? getAttribute(element, 'direction') : null;
  const flexDirection = mapDirection(directionAttr) || defaults.flexDirection;

  // Parse spacing attributes
  const paddingAttr = getAttribute(element, 'padding');
  const padding = parsePadding(paddingAttr, defaults.padding);

  const marginAttr = getAttribute(element, 'margin');
  const margin = parsePadding(marginAttr, defaults.margin);

  // Parse background attributes
  const bgAttr = getAttribute(element, 'bg');
  const backgroundColor = bgAttr ? parseColor(bgAttr) : defaults.backgroundColor;

  const bgImageAttr = getAttribute(element, 'bgImage');
  const bgImage = bgImageAttr || defaults.bgImage;

  const bgImageOpacityAttr = getAttribute(element, 'bgImageOpacity');
  const bgImageOpacity = parseInteger(bgImageOpacityAttr, defaults.bgImageOpacity);

  const bgImageScaleAttr = getAttribute(element, 'bgImageScale');
  const bgImageScale = parseInteger(bgImageScaleAttr, defaults.bgImageScale);

  const bgImageModeAttr = getAttribute(element, 'bgImageMode');
  const bgImageMode = (bgImageModeAttr as BackgroundImageMode) || defaults.bgImageMode;

  const bgImagePositionAttr = getAttribute(element, 'bgImagePosition');
  const bgImagePosition = (bgImagePositionAttr as BackgroundPosition) || defaults.bgImagePosition;

  // Parse border attributes
  const borderRadiusAttr = getAttribute(element, 'borderRadius');
  const borderRadius = parseBorderRadius(borderRadiusAttr, defaults.borderRadius);

  const borderWidthAttr = getAttribute(element, 'borderWidth');
  const borderWidth = parseInteger(borderWidthAttr, defaults.borderWidth);

  const borderColorAttr = getAttribute(element, 'borderColor');
  const borderColor = borderColorAttr ? parseColor(borderColorAttr) : defaults.borderColor;

  // Parse opacity
  const opacityAttr = getAttribute(element, 'opacity');
  const opacity = parseFloat_(opacityAttr, defaults.opacity);

  // Parse background video attributes
  const bgVideoUrlAttr = getAttribute(element, 'bgVideoUrl');
  const bgVideoUrl = bgVideoUrlAttr || defaults.bgVideoUrl;

  const bgVideoOpacityAttr = getAttribute(element, 'bgVideoOpacity');
  const bgVideoOpacity = parseInteger(bgVideoOpacityAttr, defaults.bgVideoOpacity);

  const bgVideoModeAttr = getAttribute(element, 'bgVideoMode');
  const bgVideoMode = bgVideoModeAttr || defaults.bgVideoMode;

  // Parse maxWidth with unit - 100% means "disable maxWidth" (full width)
  const maxWidthAttr = getAttribute(element, 'maxWidth');
  let maxWidth = defaults.maxWidth;
  let maxWidthUnit = defaults.maxWidthUnit;
  let disableMaxWidth = defaults.disableMaxWidth;

  if (maxWidthAttr === '100%') {
    disableMaxWidth = true;
  } else if (maxWidthAttr) {
    const parsedMaxWidth = parseValueWithUnit(
      maxWidthAttr,
      defaults.maxWidth,
      defaults.maxWidthUnit,
    );
    maxWidth =
      parsedMaxWidth.unit === '%'
        ? Math.round((parsedMaxWidth.value / 100) * 1200)
        : parsedMaxWidth.value;
    maxWidthUnit = parsedMaxWidth.unit === '%' ? 'px' : parsedMaxWidth.unit;
  }

  // Parse height
  const heightAttr = getAttribute(element, 'height');
  const height = heightAttr || defaults.height;

  // Parse position attributes
  const positionAttr = getAttribute(element, 'position');
  const position = parsePosition(positionAttr) || defaults.position;

  const topAttr = getAttribute(element, 'top');
  const top = parsePositionOffset(topAttr);

  const leftAttr = getAttribute(element, 'left');
  const left = parsePositionOffset(leftAttr);

  const rightAttr = getAttribute(element, 'right');
  const right = parsePositionOffset(rightAttr);

  const bottomAttr = getAttribute(element, 'bottom');
  const bottom = parsePositionOffset(bottomAttr);

  const zIndexAttr = getAttribute(element, 'zIndex');
  const zIndex = parseInteger(zIndexAttr, defaults.zIndex);

  // Infer position anchors state from provided values
  const positionAnchorsState = inferPositionAnchorsState(position, top, left, right, bottom);

  // Parse shadow attributes
  const shadow = parseShadowAttributes(element);

  // Parse breakpoint overrides and merge into component breakpoint defaults
  const { tablet: tabletOverrides, mobile: mobileOverrides } = parseBreakpointAttributes(
    element,
    defaults,
  );

  // Build props object with context-aware width defaults
  const props: Record<string, unknown> = {
    flexDirection,
    alignItems,
    justifyContent,
    gap,
    width: contextDefaults.width,
    height,
    minHeight: defaults.minHeight,
    minWidth: defaults.minWidth,
    maxWidth,
    maxWidthUnit,
    disableMaxWidth,
    padding,
    margin,
    borderRadius,
    borderWidth,
    shadow: defaults.shadow,
    shadowType: shadow?.shadowType ?? 'none',
    shadowSettings: shadow?.shadowSettings ?? { x: 0, y: 0, blur: 0, spread: 0, color: '#000000' },
    opacity,
    overflow: defaults.overflow,
    position,
    top: top || undefined,
    left: left || undefined,
    right: right || undefined,
    bottom: bottom || undefined,
    zIndex,
    positionAnchorsState,
    fillSpace: contextDefaults.fillSpace,
    flexBasis: contextDefaults.flexBasis,
    flexGrow: defaults.flexGrow,
    placeholderHeight: defaults.placeholderHeight,
    bgImageScale,
    bgImageOpacity,
    bgImageMode,
    bgImagePosition,
    bgVideoSourceType: defaults.bgVideoSourceType,
    bgVideoMode,
    bgVideoOpacity,
    visibility: defaults.visibility,
    aspectRatioLock: defaults.aspectRatioLock,
    tablet: { ...defaults.tablet, ...tabletOverrides },
    // Mobile always uses full-width defaults, but allow explicit overrides from XML
    mobile: { ...defaults.mobile, ...MOBILE_WIDTH_DEFAULTS, ...mobileOverrides },
  };

  // Only include optional props if they have values
  if (backgroundColor) {
    props.backgroundColor = backgroundColor;
    // Set bgGradient or bgSolid for inspector panel color field
    if (typeof backgroundColor !== 'string') {
      if (checkIsGradient(backgroundColor)) {
        props.bgGradient = backgroundColor as Gradient;
      } else {
        props.bgSolid = backgroundColor as RGBA;
      }
    }
  }
  if (bgImage) {
    props.bgImage = bgImage;
  }
  if (borderColor) {
    props.borderColor = borderColor;
  }
  if (bgVideoUrl) {
    props.bgVideoUrl = bgVideoUrl;
  }

  // Shape dividers are only supported on section elements
  if (type === 'section') {
    const shapeDividerAttr = getAttribute(element, 'shapeDivider');
    if (shapeDividerAttr && shapeDividerAttr !== 'none') {
      props.shapeDividerType = shapeDividerAttr;

      const shapeDividerPositionAttr = getAttribute(element, 'shapeDividerPosition');
      if (shapeDividerPositionAttr) {
        props.shapeDividerPosition = shapeDividerPositionAttr;
      }

      const shapeDividerHeightAttr = getAttribute(element, 'shapeDividerHeight');
      if (shapeDividerHeightAttr) {
        props.shapeDividerHeight = parseInteger(shapeDividerHeightAttr, 80);
      }

      const shapeDividerFrequencyAttr = getAttribute(element, 'shapeDividerFrequency');
      if (shapeDividerFrequencyAttr) {
        props.shapeDividerFrequency = parseInteger(shapeDividerFrequencyAttr, 3);
      }

      const shapeDividerFlipAttr = getAttribute(element, 'shapeDividerFlip');
      if (shapeDividerFlipAttr) {
        props.shapeDividerFlip = shapeDividerFlipAttr === 'true';
      }

      const shapeDividerCurveCenterAttr = getAttribute(element, 'shapeDividerCurveCenter');
      if (shapeDividerCurveCenterAttr) {
        props.shapeDividerCurveCenter = parseInteger(shapeDividerCurveCenterAttr, 50);
      }
    }
  }

  // Parse animation attributes
  const animationProps = parseAnimationAttributes(element);
  Object.assign(props, animationProps);

  return props;
};

/**
 * Transform a <section>, <row>, or <column> XML element into a CraftJS Container node
 *
 * @param element - The XML element
 * @param context - Conversion context with parent information for width calculation
 * @param type - The container type (section/row/column)
 * @param childIds - Array of child node IDs
 * @returns SerializedNode for the Container
 */
export const transformContainer = (
  element: Element,
  context: ConversionContext,
  type: ContainerType,
  childIds: string[],
): SerializedNode => {
  const props = parseContainerAttributes(element, type, context);

  // Determine custom display name based on type
  const customDisplayName = type === 'section' ? 'Section' : type === 'row' ? 'Row' : 'Column';

  return {
    type: { resolvedName: CONTENT_BLOCK_NAME },
    isCanvas: true,
    props,
    displayName: CONTENT_BLOCK_NAME,
    custom: {
      displayName: customDisplayName,
      isLocked: false,
    },
    hidden: false,
    nodes: childIds,
    linkedNodes: {},
    parent: context.parentId,
  };
};

/**
 * Transform a <section> XML element as the ROOT node
 *
 * This is used when section is the root element of the XML document.
 * The section becomes the ROOT node with parent: null.
 *
 * @param element - The <section> XML element
 * @param context - Conversion context
 * @param childIds - Array of child node IDs
 * @returns SerializedNode for the Container as ROOT
 */
export const transformSectionAsRoot = (
  element: Element,
  context: ConversionContext,
  childIds: string[],
): SerializedNode => {
  // Root section has no parent, so no context-aware width calculation needed
  const rootContext: ConversionContext = {
    ...context,
    parentFlexDirection: undefined,
    siblingCount: undefined,
    calculatedWidth: undefined,
  };
  const props = parseContainerAttributes(element, 'section', rootContext);

  return {
    type: { resolvedName: CONTENT_BLOCK_NAME },
    isCanvas: true,
    props,
    displayName: CONTENT_BLOCK_NAME,
    custom: {
      displayName: 'Section',
      isLocked: false,
    },
    hidden: false,
    nodes: childIds,
    linkedNodes: {},
    parent: null,
  };
};

/**
 * Map align attribute value to CSS alignItems value
 */
const mapAlignJustify = (value: string | null): string | undefined => {
  if (!value) return undefined;

  const normalized = value.toLowerCase().trim();
  switch (normalized) {
    case 'start':
    case 'flex-start':
      return 'flex-start';
    case 'center':
      return 'center';
    case 'end':
    case 'flex-end':
      return 'flex-end';
    // Note: B4 doesn't actually support this yet but including for AI generation for better results
    case 'stretch':
      return 'stretch';
    default:
      return undefined;
  }
};

/**
 * Map direction attribute value to CSS flexDirection value
 */
const mapDirection = (value: string | null): 'row' | 'column' | undefined => {
  if (!value) return undefined;

  const normalized = value.toLowerCase().trim();
  if (normalized === 'row' || normalized === 'row-reverse') {
    return 'row';
  }
  if (normalized === 'column' || normalized === 'column-reverse') {
    return 'column';
  }

  return undefined;
};

/**
 * Parse animation, hover effect, and stagger attributes from a container element
 */
const parseAnimationAttributes = (element: Element): Record<string, unknown> => {
  const props: Record<string, unknown> = {};

  const animationTypeAttr = getAttribute(element, 'animationType');
  if (animationTypeAttr && (ANIMATION_TYPES as readonly string[]).includes(animationTypeAttr)) {
    props.animationType = animationTypeAttr as AnimationType;
  }

  const animationDurationAttr = getAttribute(element, 'animationDuration');
  if (animationDurationAttr) {
    props.animationDuration = parseInteger(animationDurationAttr, 600);
  }

  const animationDelayAttr = getAttribute(element, 'animationDelay');
  if (animationDelayAttr) {
    props.animationDelay = parseInteger(animationDelayAttr, 0);
  }

  const animationEasingAttr = getAttribute(element, 'animationEasing');
  if (
    animationEasingAttr &&
    (ANIMATION_EASINGS as readonly string[]).includes(animationEasingAttr)
  ) {
    props.animationEasing = animationEasingAttr as AnimationEasing;
  }

  const hoverEffectAttr = getAttribute(element, 'hoverEffect');
  if (hoverEffectAttr && (HOVER_EFFECT_TYPES as readonly string[]).includes(hoverEffectAttr)) {
    props.hoverEffect = hoverEffectAttr as HoverEffectType;
  }

  const hoverTransitionDurationAttr = getAttribute(element, 'hoverTransitionDuration');
  if (hoverTransitionDurationAttr) {
    props.hoverTransitionDuration = parseInteger(hoverTransitionDurationAttr, 300);
  }

  const hoverIntensityAttr = getAttribute(element, 'hoverIntensity');
  if (hoverIntensityAttr) {
    props.hoverIntensity = parseInteger(hoverIntensityAttr, 105);
  }

  const staggerDelayAttr = getAttribute(element, 'staggerDelay');
  if (staggerDelayAttr) {
    props.staggerDelay = parseInteger(staggerDelayAttr, 100);
  }

  return props;
};

/**
 * Valid attributes for container elements (section/row/column)
 */
export const CONTAINER_ATTRIBUTES = [
  'bg',
  'bgImage',
  'bgImageOpacity',
  'bgImageScale',
  'bgImageMode',
  'bgImagePosition',
  'bgVideoUrl',
  'bgVideoOpacity',
  'bgVideoMode',
  'padding',
  'margin',
  'gap',
  'align',
  'justify',
  'borderRadius',
  'borderWidth',
  'borderColor',
  'opacity',
  'direction',
  'width',
  'height',
  'maxWidth',
  // Shadow attributes
  ...SHADOW_ATTRIBUTES,
  // Position attributes
  'position',
  'top',
  'left',
  'right',
  'bottom',
  'zIndex',
  // Shape divider attributes (section elements only)
  'shapeDivider',
  'shapeDividerPosition',
  'shapeDividerHeight',
  'shapeDividerFrequency',
  'shapeDividerFlip',
  'shapeDividerCurveCenter',
  // Animation attributes
  'animationType',
  'animationDuration',
  'animationDelay',
  'animationEasing',
  'hoverEffect',
  'hoverTransitionDuration',
  'hoverIntensity',
  'staggerDelay',
] as const;

/**
 * Attributes that can be overridden per breakpoint (tablet/mobile)
 * These map to CraftJS detachable properties for containers
 */
export const BREAKPOINT_CONTAINER_ATTRIBUTES = [
  'padding',
  'margin',
  'gap',
  'direction',
  'justify',
  'align',
  'width',
  'borderRadius',
  'height',
  'maxWidth',
  // Position attributes
  'position',
  'top',
  'left',
  'right',
  'bottom',
  'zIndex',
] as const;

/**
 * Check if an attribute is valid for container elements
 * Handles both base attributes and breakpoint-prefixed attributes (tablet-*, mobile-*)
 */
export const isValidContainerAttribute = (attr: string): boolean => {
  // Check base attributes
  if ((CONTAINER_ATTRIBUTES as readonly string[]).includes(attr)) {
    return true;
  }

  // Check breakpoint-prefixed attributes (tablet-padding, mobile-gap, etc.)
  let propName: string | null = null;
  if (attr.startsWith('tablet-')) {
    propName = attr.slice(7);
  } else if (attr.startsWith('mobile-')) {
    propName = attr.slice(7);
  }

  if (propName) {
    return (BREAKPOINT_CONTAINER_ATTRIBUTES as readonly string[]).includes(propName);
  }

  return false;
};
